/**
 * AuthGradientButton — the auth CTA, now a thin adapter over `components/ui/Button`.
 *
 * WHY THIS FILE STILL EXISTS
 *
 * The auth screens call this component by name, with `variant: 'primary' |
 * 'secondary'` and an optional `entering`. It used to carry its OWN button
 * anatomy — a purple→red gradient fill, its own box, its own label rules —
 * which is exactly why the auth CTAs looked like a different product from every
 * other primary button in the app: the shared `Button` is coral with a luminous
 * glass edge, and this was a gradient slab. Two primaries, two brands.
 *
 * It now renders the single CTA primitive and keeps only the two things the
 * shared `Button` does not model: the full-width box every auth screen lays out
 * against, and the Reanimated `entering` wrapper some of them animate in with.
 * The label rules (shrink-to-fit at the design baseline, grow the box above it)
 * live in `Button` now — one owner, so they cannot drift apart again.
 *
 * `Button` default size is `md`; the auth CTA has always been a 56dp `lg`, so
 * the height is pinned here to keep the auth screens pixel-identical.
 */
import Animated, { type AnimatedProps } from 'react-native-reanimated'
import type { ViewProps } from 'react-native'
import { Button } from '../ui/Button'

interface AuthGradientButtonProps {
  title: string
  onPress: () => void
  loading?: boolean
  disabled?: boolean
  testID?: string
  /** 'secondary' renders the shared outlined secondary instead of the primary fill */
  variant?: 'primary' | 'secondary'
  /** Reanimated entering animation (wraps in Animated.View if provided) */
  entering?: AnimatedProps<ViewProps>['entering']
}

export function AuthGradientButton({
  title,
  onPress,
  loading = false,
  disabled = false,
  testID,
  variant = 'primary',
  entering,
}: AuthGradientButtonProps) {
  const button = (
    <Button
      title={title}
      onPress={onPress}
      loading={loading}
      disabled={disabled}
      variant={variant}
      size="lg"
      testID={testID}
      style={{ width: '100%' }}
    />
  )

  if (entering) {
    return (
      <Animated.View collapsable={false} entering={entering}>
        {button}
      </Animated.View>
    )
  }

  return button
}
